import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api";
import { createAsyncThunkInstance } from "../createAsyncThunkInstance";
import {
  ApplicationViewRequest,
  ApplicationViewResponse,
  ApplicationCreateRequest,
  ApplicationCreateResponse,
  ApplicationsRequest,
  ApplicationsResponse,
  ApplicationUpdateRequest,
  ApplicationUpdateResponse,
} from "../../api/applications/types";
import { ICommonInitialState } from "../../shared/types/storeTypes";
import { AxiosError } from "axios";
import { Application } from "../../model/applications";

interface IApplicationsState extends ICommonInitialState {
  applications: ApplicationsResponse;
  currentApplication: Application | null;
}

const initialState: IApplicationsState = {
  applications: {
    count: 0,
    items: [],
    can_add: false,
    can_edit: false,
    can_manage_channel: false,
    count_unsorted: 0,
    count_appointed: 0,
  },
  currentApplication: null,
  isLoading: false,
  error: null,
};

export const fetchAllApplications = createAsyncThunkInstance<ApplicationsResponse, ApplicationsRequest>(
  "applications/fetchAllApplications",
  async ({ params }) => {
    const response = await api.applications.applications({ params });
    return response.data;
  },
);

export const fetchSingleApplication = createAsyncThunkInstance<ApplicationViewResponse, ApplicationViewRequest>(
  "applications/fetchSingleApplication",
  (data) => api.applications.applicationView(data),
);

export const fetchCreateApplication = createAsyncThunk<ApplicationCreateResponse, ApplicationCreateRequest, { rejectValue: AxiosError }>(
  "applications/fetchCreateApplication",
  async (data, { rejectWithValue }) => {
    try {
      const { data: response } = await api.applications.applicationCreate(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  },
);

export const fetchUpdateApplication = createAsyncThunk<ApplicationUpdateResponse, ApplicationUpdateRequest, { rejectValue: AxiosError }>(
  "applications/fetchUpdateApplication",
  async (data, { rejectWithValue }) => {
    try {
      const { data: response } = await api.applications.applicationUpdate(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  },
);

const applicationsSlice = createSlice({
  name: "applications",
  initialState,
  reducers: {
    clearSingleApplication: (state) => {
      state.currentApplication = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllApplications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllApplications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.applications = action.payload;
      })
      .addCase(fetchCreateApplication.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCreateApplication.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentApplication = action.payload;
      })
      .addCase(fetchSingleApplication.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleApplication.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentApplication = action.payload;
      });
  },
});

export const applicationsReducer = applicationsSlice.reducer;
export const { clearSingleApplication } = applicationsSlice.actions;
